import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Paper, LinearProgress, Collapse,
  Select, FormControl, InputLabel, Tooltip, Alert, useTheme, alpha, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TableSortLabel,
} from '@mui/material';
import useSortableTable from '../utils/useSortableTable';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Cancel as CancelIcon,
  RestartAlt as RestartIcon,
  Delete as DeleteIcon,
  PriorityHigh as PriorityIcon,
  Send as DeliverIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  fetchJobs, fetchJobStats, cancelJob, deleteJob, restartJob, updateJobPriority,
} from '../store/slices/jobsSlice';
import DeliverDialog from '../components/DeliverDialog';
import PageHeader from '../components/PageHeader';
import StatCards from '../components/StatCards';
import websocketService from '../services/websocket';

const STATUS_COLORS = {
  pending: 'default', processing: 'warning',
  completed: 'success', failed: 'error', cancelled: 'default',
};
const STATUS_LABELS = {
  pending: 'Pending', processing: 'Processing',
  completed: 'Completed', failed: 'Failed', cancelled: 'Cancelled',
};
const PRIORITY_COLORS = {
  urgent: 'error', high: 'warning', normal: 'default', low: 'info',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
}
function fmtDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}
function fmtETA(seconds) {
  if (seconds == null || seconds < 0) return null;
  if (seconds < 45) return '<1 min';
  if (seconds < 3600) return `~${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
}
function fmtDuration(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function DetailRow({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', py: 0.5, gap: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', width: 140, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value || '—'}</Typography>
    </Box>
  );
}




function Jobs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { jobs, stats } = useSelector((state) => state.jobs);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [deliverJob, setDeliverJob] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadData = useCallback(() => {
    dispatch(fetchJobs({}));
    dispatch(fetchJobStats());
  }, [dispatch]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      if (!document.hidden) loadData();
    }, 3000);
    websocketService.subscribe(['job.created', 'job.updated', 'job.progress', 'job.completed', 'job.failed', 'job.assigned']);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleMenuOpen = (event, job) => { event.stopPropagation(); setAnchorEl(event.currentTarget); setSelectedJob(job); };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedJob(null); };
  const handleCancel = () => { if (selectedJob) dispatch(cancelJob(selectedJob.id)); handleMenuClose(); };
  const handleRestart = () => { if (selectedJob) dispatch(restartJob(selectedJob.id)); handleMenuClose(); };
  const handleDelete = () => { setDeleteConfirm(selectedJob); handleMenuClose(); };
  const handlePriority = (priority) => { if (selectedJob) dispatch(updateJobPriority({ jobId: selectedJob.id, priority })); handleMenuClose(); };

  // A watermark "job" has two transcoding phases: the MP4 watermark
  // encode (jobs.status) and the HLS streaming encode (hls_status).
  // The queue should show every row that has unfinished work in EITHER
  // phase, so an operator sees "Encoding stream 12%" rows the same way
  // they see "Processing" rows. Only jobs where MP4 is COMPLETED *and*
  // HLS is also done/skipped fall off into the Library.
  const ACTIVE_MP4 = new Set(['pending', 'processing', 'failed', 'cancelled']);
  const HLS_ACTIVE = new Set(['queued', 'starting', 'encoding', 'failed']);
  const activeJobs = (jobs || []).filter((j) => {
    if (ACTIVE_MP4.has(j.status)) return true;
    return j.status === 'completed' && HLS_ACTIVE.has(j.hls_status);
  });
  // The status filter is a virtual concept that spans both phases.
  // 'pending'/'processing'/etc match the MP4 phase; 'streaming' matches
  // anything in the HLS phase.
  const filteredJobsRaw = (() => {
    let rows = activeJobs;
    if (statusFilter) {
      rows = rows.filter(j => {
        if (statusFilter === 'streaming') return j.status === 'completed' && HLS_ACTIVE.has(j.hls_status);
        return j.status === statusFilter;
      });
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      rows = rows.filter(j =>
        (j.recipient_name || '').toLowerCase().includes(q) ||
        (j.recipient_email || '').toLowerCase().includes(q) ||
        (j.source_filename || '').toLowerCase().includes(q)
      );
    }
    return rows;
  })();

  // Default queue order, designed to mirror the actual dispatch order
  // an operator can predict from the row layout:
  //   0. Actively encoding (MP4 processing OR HLS encoding/starting),
  //      sorted by progress desc — closer to done is on top.
  //   1. MP4 watermark pending — these always claim before any HLS work
  //      (the agent's claim endpoint checks watermark first), so they
  //      surface above HLS-queued. Within: priority desc, FIFO.
  //   2. HLS queued (Phase 2) — sorted by `hls_queue_position`, the
  //      server-side FIFO position over HLSJob.created_at, so the row
  //      order matches the actual pipeline order.
  //   3. Failed — most recent first, needs operator attention.
  //   4. Cancelled — most recent first, anchored at the bottom.
  // Click any column header to override; this is just the default.
  const PRIORITY_WEIGHT = { urgent: 4, high: 3, normal: 2, low: 1 };
  const queueBucket = (j) => {
    const inHls = j.status === 'completed' && HLS_ACTIVE.has(j.hls_status);
    if (inHls) {
      if (j.hls_status === 'encoding' || j.hls_status === 'starting') return 0;
      if (j.hls_status === 'queued') return 2;
      if (j.hls_status === 'failed') return 3;
    }
    if (j.status === 'processing') return 0;
    if (j.status === 'pending') return 1;
    if (j.status === 'failed') return 3;
    if (j.status === 'cancelled') return 4;
    return 5;
  };
  const tieByPriority = (a, b) => {
    const pA = PRIORITY_WEIGHT[a.priority] || 2;
    const pB = PRIORITY_WEIGHT[b.priority] || 2;
    return pB - pA;
  };
  const tieByCreatedAsc = (a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return ta - tb;
  };
  const tieByCreatedDesc = (a, b) => -tieByCreatedAsc(a, b);
  const sortedFilteredJobs = [...filteredJobsRaw].sort((a, b) => {
    const bA = queueBucket(a);
    const bB = queueBucket(b);
    if (bA !== bB) return bA - bB;
    if (bA === 0) {
      // In-progress: closer to done first.
      const pA = a.status === 'processing' ? (a.progress || 0) : (a.hls_progress || 0);
      const pB = b.status === 'processing' ? (b.progress || 0) : (b.hls_progress || 0);
      return pB - pA;
    }
    if (bA === 1) {
      // MP4 pending: priority desc, FIFO.
      const p = tieByPriority(a, b);
      return p !== 0 ? p : tieByCreatedAsc(a, b);
    }
    if (bA === 2) {
      // HLS queued: priority desc, then server-computed queue position
      // asc so the UI matches the actual claim order. created_at as a
      // last-resort fallback for any row missing a position.
      const p = tieByPriority(a, b);
      if (p !== 0) return p;
      const qA = a.hls_queue_position || 999999;
      const qB = b.hls_queue_position || 999999;
      if (qA !== qB) return qA - qB;
      return tieByCreatedAsc(a, b);
    }
    return tieByCreatedDesc(a, b);
  });

  // useSortableTable with a null default key passes the array through
  // unchanged, so our pre-sort above wins until the operator clicks a
  // header (at which point that column's sort takes over).
  const { rows: filteredJobs, sortProps } = useSortableTable(
    sortedFilteredJobs,
    null,
    'desc',
    {
      source_filename: (r) => (r.source_filename || '').toLowerCase(),
      created_at: (r) => (r.created_at ? new Date(r.created_at).getTime() : 0),
      progress: (r) => Number(r.progress || 0),
      status: (r) => queueBucket(r),
    },
  );

  return (
    <Box>
      <PageHeader
        title="Watermark Encodes"
        actions={
          <>
            <Tooltip title="Refresh"><IconButton onClick={loadData}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/screeners/create')}>Create Watermark Encode</Button>
          </>
        }
      />

      <StatCards
        stats={stats}
        fallback={{
          // Client-derived fallback so the cards reflect the table
          // immediately even before /api/jobs/stats settles.
          pending: jobs.filter(j => j.status === 'pending').length,
          processing: jobs.filter(j => j.status === 'processing').length,
          completed: jobs.filter(j => j.status === 'completed').length,
          failed: jobs.filter(j => j.status === 'failed').length,
        }}
      />

      {/* Operator-facing reassurance: a real user reported "not sure
          how to begin encode once something has been added to the
          queue." There is no "begin" button — the dispatch loop picks
          jobs up automatically every 5 seconds. This banner says that
          out loud so nobody waits for a button that doesn't exist. */}
      <Alert severity="info" variant="outlined" sx={{ mb: 2, borderColor: 'divider' }}>
        Encoding starts automatically — once a screener is in the queue, an encoder will pick it up within seconds. Nothing else is required from you. You'll get a notification when it's ready, or watch it appear in the Distribution / Library view as each phase completes.
      </Alert>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by recipient or filename"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="streaming">Encoding</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        {(() => {
          const active = (filteredJobs || []).filter(j => j.status === 'pending' || j.status === 'processing');
          if (active.length === 0) return null;
          const queueDrainSec = Math.max(0, ...active.map(j => j.eta_seconds || 0));
          const eta = fmtETA(queueDrainSec);
          if (!eta) return null;
          return (
            <Chip
              label={`Queue empties in ${eta}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          );
        })()}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell><TableSortLabel {...sortProps('source_filename')}>Source / Recipient</TableSortLabel></TableCell>
              <TableCell sx={{ width: 120 }}><TableSortLabel {...sortProps('status')}>Status</TableSortLabel></TableCell>
              <TableCell sx={{ width: 160 }}><TableSortLabel {...sortProps('progress')}>Progress</TableSortLabel></TableCell>
              <TableCell sx={{ width: 130 }}><TableSortLabel {...sortProps('created_at')}>Created</TableSortLabel></TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredJobs.map((job) => {
              const isExpanded = expandedId === job.id;
              const startTime = job.started_at ? new Date(job.started_at) : null;
              const endTime = job.completed_at ? new Date(job.completed_at) : null;
              const duration = startTime && endTime ? endTime - startTime : null;
              // Phase resolution: the row renders the active phase. If the
              // MP4 watermark encode is still in flight, show that. Once
              // it's COMPLETED we fall through to the HLS phase, so an
              // "Encoding stream 12%" row sits alongside "Processing" /
              // "Pending" / "Failed" without an awkward second queue.
              const inHlsPhase = job.status === 'completed' && HLS_ACTIVE.has(job.hls_status);
              // Single-phase architecture (v9.12.0+): HLS is the only
              // encode pass. Watermark filter is applied inline by the
              // encoder during HLS encoding. Progress is just the HLS
              // progress (0-100%); no two-phase mapping needed.
              // _phaseDetail unused for now — retained for an upcoming
              // detail row UI; underscore prefix opts out of unused-var lint.
              let phaseLabel, phaseColor, phasePct, _phaseDetail;
              if (inHlsPhase) {
                if (job.hls_status === 'encoding') { phaseLabel = 'Encoding'; phaseColor = 'warning'; }
                else if (job.hls_status === 'starting') { phaseLabel = 'Starting'; phaseColor = 'info'; }
                else if (job.hls_status === 'failed') { phaseLabel = 'Failed'; phaseColor = 'error'; }
                else { phaseLabel = 'Queued'; phaseColor = 'default'; }
                const hlsPct = Math.max(0, Math.min(100, Number(job.hls_progress) || 0));
                phasePct = hlsPct;
                _phaseDetail = null;
              } else {
                phaseLabel = STATUS_LABELS[job.status] || job.status;
                phaseColor = STATUS_COLORS[job.status] || 'default';
                if (job.status === 'completed') {
                  phasePct = 100;
                  _phaseDetail = null;
                } else if (job.status === 'pending' || job.status === 'cancelled' || job.status === 'failed') {
                  phasePct = 0;
                  _phaseDetail = null;
                } else {
                  // Legacy in-flight Phase-1 jobs (pre-v9.12.0). Show
                  // raw progress.
                  phasePct = Math.max(0, Math.min(100, Number(job.progress) || 0));
                  _phaseDetail = null;
                }
              }
              return (
                <React.Fragment key={job.id}>
                  <TableRow
                    hover
                    onClick={() => setExpandedId(isExpanded ? null : job.id)}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                  >
                    <TableCell>
                      <IconButton size="small" sx={{ p: 0 }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{job.source_filename}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.recipient_name ? `${job.recipient_name}` : ''}
                        {job.recipient_email ? ` <${job.recipient_email}>` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* The encoder assignment lives in the expanded
                          details panel ("Encoder" row). Showing it
                          here too duplicates the same fact on the
                          same screen — see feedback_no_duplicate_ui. */}
                      <Chip label={phaseLabel} color={phaseColor} size="small" />
                    </TableCell>
                    <TableCell>
                      {/* A row that hasn't actually started any work yet
                          shouldn't display a 0% progress bar — that
                          reads as "stalled" rather than "waiting in
                          line". Show "Queued (#N)" instead. */}
                      {(() => {
                        const isWaiting =
                          job.status === 'pending'
                          || (inHlsPhase && job.hls_status === 'queued');
                        if (isWaiting) {
                          const pos = inHlsPhase ? job.hls_queue_position : null;
                          return (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                Queued{pos ? ` (#${pos})` : ''}
                              </Typography>
                              {!inHlsPhase && fmtETA(job.eta_seconds) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                  ETA {fmtETA(job.eta_seconds)}
                                </Typography>
                              )}
                            </>
                          );
                        }
                        return (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress variant="determinate" value={Math.min(100, phasePct)}
                                sx={{ height: 6, borderRadius: 3, flexGrow: 1 }} />
                              <Typography variant="caption" sx={{ minWidth: 30 }}>
                                {Math.round(phasePct)}%
                              </Typography>
                            </Box>
                            {!inHlsPhase && fmtETA(job.eta_seconds) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                ETA {fmtETA(job.eta_seconds)}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmtDateShort(job.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, job)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail */}
                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={6}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2 }}>
                          <Grid container spacing={3}>
                            {/* Job + Encoding settings */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Job</Typography>
                              <DetailRow label="Job ID" value={`#${job.id}`} />
                              <DetailRow label="Unique ID" value={job.unique_id} mono />
                              <DetailRow label="Status" value={STATUS_LABELS[job.status] || job.status} />
                              <DetailRow label="Priority" value={
                                <Chip label={job.priority || 'normal'} size="small" color={PRIORITY_COLORS[job.priority] || 'default'} variant="outlined" />
                              } />
                              {/* Phase-aware encoder display. The
                                  jobs.encoder_id holds whichever encoder
                                  did the MP4 watermark phase (which can
                                  be days or weeks old). When the row is
                                  in HLS phase, show that phase's
                                  encoder instead — otherwise the queue
                                  reads as "still on Encode01" when
                                  Encode04 is actually doing the work. */}
                              <DetailRow label="Encoder" value={(() => {
                                const phase2 = job.status === 'completed' && HLS_ACTIVE.has(job.hls_status);
                                const id = phase2 ? job.hls_encoder_id : job.encoder_id;
                                if (!id) return 'Unassigned';
                                return `Encoder #${id}` + (phase2 ? ' (HLS)' : '');
                              })()} />
                              <DetailRow label="Retries" value={`${job.retry_count || 0} / ${job.max_retries || 3}`} />

                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Encoding</Typography>
                              <DetailRow label="Profile" value={job.encoding_profile?.name || job.encoding_profile_name || '—'} />
                              {job.encoding_profile && (
                                <>
                                  <DetailRow label="Codec" value={job.encoding_profile.video_codec} />
                                  <DetailRow label="Preset" value={job.encoding_profile.preset} />
                                  <DetailRow label="CRF" value={job.encoding_profile.crf} />
                                  {job.encoding_profile.output_resolution && (
                                    <DetailRow label="Output Res" value={job.encoding_profile.output_resolution} />
                                  )}
                                </>
                              )}
                              <DetailRow label="Template" value={job.template?.name || job.template_name || '—'} />
                              {job.template && (
                                <>
                                  <DetailRow label="Font" value={job.template.font_name ? `${job.template.font_name}${job.template.font_size ? ` @ ${job.template.font_size}px` : ''}` : '—'} />
                                  <DetailRow label="Color" value={job.template.font_color || '—'} mono />
                                  <DetailRow label="Opacity" value={job.template.opacity != null ? `${Math.round(job.template.opacity * 100)}%` : '—'} />
                                  <DetailRow label="Position" value={
                                    job.template.position_type === 'moving'
                                      ? `Moving (${(job.template.positions || []).length} steps)`
                                      : job.template.position_type === 'static'
                                        ? `Static (${(job.template.positions || []).length} overlay${(job.template.positions || []).length === 1 ? '' : 's'})`
                                        : (job.template.position_type || '—')
                                  } />
                                </>
                              )}
                            </Grid>

                            {/* Source / Output / Recipient */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Source</Typography>
                              <DetailRow label="Filename" value={job.source_filename} mono />
                              {job.encoding_settings?.source_resolution && (
                                <DetailRow label="Resolution" value={job.encoding_settings.source_resolution} />
                              )}
                              {job.encoding_settings?.source_fps && (
                                <DetailRow label="Frame Rate" value={`${job.encoding_settings.source_fps} fps`} />
                              )}
                              {job.encoding_settings?.source_duration_seconds && (
                                <DetailRow label="Duration" value={fmtDuration(job.encoding_settings.source_duration_seconds * 1000)} />
                              )}

                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Output</Typography>
                              <DetailRow label="Filename" value={job.output_filename} mono />
                              {job.output_size_bytes && (
                                <DetailRow label="Size" value={(job.output_size_bytes / (1024 ** 3)).toFixed(2) + ' GB'} />
                              )}
                              {job.share_url && (
                                <DetailRow label="Share URL" value={job.share_url} mono />
                              )}

                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Recipient</Typography>
                              <DetailRow label="Name" value={job.recipient_name} />
                              <DetailRow label="Email" value={job.recipient_email} />
                              <DetailRow label="Company" value={(() => {
                                if (!job.company) return job.company_name || '—';
                                const style = job.encoding_settings?.company_label_style;
                                if (style === 'short' && job.company.short_name) return job.company.short_name;
                                return job.company.name;
                              })()} />
                            </Grid>

                            {/* Timing */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Timing</Typography>
                              <DetailRow label="Created" value={fmtDate(job.created_at)} />
                              <DetailRow label="Started" value={fmtDate(job.started_at)} />
                              <DetailRow label="Completed" value={fmtDate(job.completed_at)} />
                              <DetailRow label="Duration" value={duration ? fmtDuration(duration) : (job.status === 'processing' ? 'In progress' : '—')} />
                              {(() => {
                                const srcDur = job.encoding_settings?.source_duration_seconds;
                                if (srcDur && duration) {
                                  const ratio = (srcDur * 1000) / duration;
                                  return <DetailRow label="Encode Speed" value={`${ratio.toFixed(2)}x realtime`} />;
                                }
                                return null;
                              })()}
                              {job.error_message && (
                                <Box sx={{ mt: 1, p: 1.5, bgcolor: alpha(theme.palette.error.main, 0.08), borderRadius: 1, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                                  <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{job.error_message}</Typography>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {filteredJobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Queue is empty{statusFilter ? ` (no ${statusFilter} jobs)` : ''}. Completed jobs are in the Library.
                  </Typography>
                  <Button size="small" onClick={() => navigate('/screeners/create')} sx={{ mt: 1 }}>Create Watermark Encode</Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* HLS rows are folded into the main queue above as Phase 2 of 2 —
          the streaming encode is just another phase of the same watermark
          job, not a separate workstream. */}


      {/* Actions menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedJob?.status === 'completed' && !HLS_ACTIVE.has(selectedJob?.hls_status) && (
          <MenuItem onClick={() => { setDeliverJob(selectedJob); handleMenuClose(); }}>
            <ListItemIcon><DeliverIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Deliver</ListItemText>
          </MenuItem>
        )}
        {/* Priority is per-job and drives BOTH phases (the watermark
            claim and the HLS claim both ORDER BY jobs.priority), so
            offer the priority chooser whenever the job has unfinished
            work in either phase. */}
        {(selectedJob?.status === 'pending'
          || (selectedJob?.status === 'completed' && HLS_ACTIVE.has(selectedJob?.hls_status))) && [
          <MenuItem key="urgent" onClick={() => handlePriority('urgent')} selected={selectedJob?.priority === 'urgent'}>
            <ListItemIcon><PriorityIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Urgent Priority</ListItemText>
          </MenuItem>,
          <MenuItem key="high" onClick={() => handlePriority('high')} selected={selectedJob?.priority === 'high'}>
            <ListItemIcon><PriorityIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText>High Priority</ListItemText>
          </MenuItem>,
          <MenuItem key="normal" onClick={() => handlePriority('normal')} selected={selectedJob?.priority === 'normal'}>
            <ListItemIcon><PriorityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Normal Priority</ListItemText>
          </MenuItem>,
          <MenuItem key="low" onClick={() => handlePriority('low')} selected={selectedJob?.priority === 'low'}>
            <ListItemIcon><PriorityIcon fontSize="small" color="disabled" /></ListItemIcon>
            <ListItemText>Low Priority</ListItemText>
          </MenuItem>,
        ]}
        {(selectedJob?.status === 'failed' || selectedJob?.status === 'cancelled') && (
          <MenuItem onClick={handleRestart}>
            <ListItemIcon><RestartIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Restart</ListItemText>
          </MenuItem>
        )}
        {['pending', 'processing'].includes(selectedJob?.status) && (
          <MenuItem onClick={handleCancel}>
            <ListItemIcon><CancelIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Cancel</ListItemText>
          </MenuItem>
        )}
        {['completed', 'failed', 'cancelled'].includes(selectedJob?.status) && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {deliverJob && (
        <DeliverDialog
          open={Boolean(deliverJob)}
          onClose={() => setDeliverJob(null)}
          sourceType="screener"
          sourceJobId={deliverJob.id}
          projectName={deliverJob.source_filename?.replace(/\.[^/.]+$/, '') || 'Screener'}
          filename={deliverJob.output_filename || deliverJob.source_filename}
          /* No nasPath — backend resolves it from sourceJobId so the per-company subdir is used */
          recipientName={deliverJob.recipient_name}
          recipientEmail={deliverJob.recipient_email}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Watermark Encode Job?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteConfirm?.source_filename}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will permanently remove the job record and watermarked output file.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
            This operation cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            dispatch(deleteJob(deleteConfirm.id));
            setDeleteConfirm(null);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Jobs;
